import React, { useEffect, useMemo, useRef, useState } from "react";
import { SkeletonText } from "../shared/Skeleton";
import {
  ACCOMMODATION_COLORS,
  formatMonthShort,
  formatDayOfMonth,
  formatAttendance,
  formatCategory,
  formatSubcategory,
  dateSortKey,
  ScoreBadge,
  AccommodationPill,
  SourceDot,
  ExportMenu,
  downloadCsv,
  downloadPdf,
} from "./shared";
import ThinScroll from "../shared/ThinScroll";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import { expandableRowProps } from "../shared/expandableRow";

const EFFORT_LABELS = { campaign: "Campaign", quick_win: "Quick Win", monitor: "Monitor", low: "Low" };
const EFFORT_CLASSES = {
  campaign: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
  quick_win: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300",
  monitor: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
  low: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400",
};

const scoreToEffort = (score) => {
  const numericScore = Number(score || 0);
  if (numericScore >= 70) return "campaign";
  if (numericScore >= 50) return "quick_win";
  if (numericScore >= 30) return "monitor";
  return "low";
};

const computeAttendanceScore = (attendance) => {
  const numericAttendance = Number(attendance || 0);
  if (numericAttendance <= 0) return 0;
  return Math.min(100, Math.log10(numericAttendance) * 25);
};

const computeLeadTimeScore = (days) => Math.min(100, Math.max(0, Number(days || 0)) * 2);
const computeDurationScore = (days) => Math.min(100, Math.max(0, Number(days || 0)) * 20);

const EffortBadge = ({ score }) => {
  const tier = scoreToEffort(score);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${EFFORT_CLASSES[tier]}`}>
      {EFFORT_LABELS[tier]}
    </span>
  );
};

const LEAD_TIME_CLASSES = {
  reactive: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300",
  tight: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
  ample: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
};

const LeadTimeBadge = ({ days }) => {
  if (days == null) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  const numericDays = Number(days);
  const tier = numericDays < 14 ? "reactive" : numericDays < 30 ? "tight" : "ample";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums ${LEAD_TIME_CLASSES[tier]}`}>
      {numericDays}d
    </span>
  );
};

// Columns with no sortKey (Event, Venue, Best, Effort) render as plain headers —
// the sortable ones wrap their label in a real <button> so the control is
// keyboard-reachable, and carry `aria-sort` so the direction is announced. An
// onClick on a bare <th> does neither.
const SortHeader = ({ label, sortKey, sortState, setSortState, align = "left" }) => {
  const active = sortState.key === sortKey;
  const dir = active ? sortState.dir : null;
  const baseClass = `px-3 py-2 text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 ${align === "right" ? "text-right" : "text-left"}`;

  if (!sortKey) {
    return <th scope="col" className={baseClass}>{label}</th>;
  }

  const onClick = () => {
    setSortState(
      active
        ? { key: sortKey, dir: dir === "asc" ? "desc" : "asc" }
        : { key: sortKey, dir: "desc" }
    );
  };

  return (
    <th
      scope="col"
      className={baseClass}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={onClick}
        title={`Sort by ${label}`}
        className={`inline-flex items-center gap-1 select-none uppercase tracking-wider font-semibold hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/40 rounded-sm ${align === "right" ? "justify-end w-full" : ""}`}
      >
        {label}
        {active && (
          <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">{dir === "asc" ? "▲" : "▼"}</span>
        )}
      </button>
    </th>
  );
};

const SubScoreBar = ({ label, weight, fill, detail }) => {
  const pct = Math.max(0, Math.min(100, Number(fill || 0)));
  return (
    <div className="text-xs space-y-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="text-gray-600 dark:text-gray-300">
          <span className="font-semibold">{label}</span>
          <span className="text-gray-400 dark:text-gray-500 ml-1">(×{weight})</span>
        </div>
        <div className="text-right text-gray-700 dark:text-gray-200 font-medium">
          {detail}
        </div>
      </div>
      <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div className="h-full bg-[#32B1FF]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const ScoreBreakdown = ({ row, onOpenPlaybook }) => {
  const affinityRaw = Number(row.best_affinity || 0);
  const affinityFill = affinityRaw * 100;
  const proxFill = Number(row.proximity_score || 0);
  const attendanceFill = computeAttendanceScore(row.attendance_estimate);
  const leadFill = computeLeadTimeScore(row.lead_time_days);
  const durationFill = computeDurationScore(row.event_duration_days);

  const affinityDetail = `${affinityRaw.toFixed(2)}${row.best_accommodation ? ` ${row.best_accommodation}` : ""}`;
  const proxDetail = `${Math.round(proxFill)} (${Number(row.distance_miles || 0).toFixed(1)} mi)`;
  const attendanceDetail = row.attendance_estimate
    ? `${Math.round(attendanceFill)} (${formatAttendance(row.attendance_estimate)})`
    : "—";
  const leadDetail = `${Math.round(leadFill)} (${Number(row.lead_time_days || 0)} day${Number(row.lead_time_days) === 1 ? "" : "s"})`;
  const durationDays = Number(row.event_duration_days || 0);
  const durationDetail = `${Math.round(durationFill)} (${durationDays} day${durationDays === 1 ? "" : "s"})`;

  return (
    <div className="bg-gray-50 dark:bg-gray-950/40 border-t border-gray-200 dark:border-gray-800 px-6 py-5 space-y-4 !text-left">
      <div className="space-y-1">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
          Score Breakdown
        </div>
        <div className="flex items-center gap-3">
          <span className="flex-1 min-w-0 text-gray-700 dark:text-gray-200 font-semibold text-sm">
            {row.event_name}
          </span>
          <span className="shrink-0">
            <ScoreBadge score={Number(row.opportunity_score || 0)} />
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <SubScoreBar label="Affinity" weight="0.35" fill={affinityFill} detail={affinityDetail} />
        <SubScoreBar label="Proximity" weight="0.25" fill={proxFill} detail={proxDetail} />
        <SubScoreBar label="Attendance" weight="0.15" fill={attendanceFill} detail={attendanceDetail} />
        <SubScoreBar label="Lead Time" weight="0.15" fill={leadFill} detail={leadDetail} />
        <SubScoreBar label="Duration" weight="0.10" fill={durationFill} detail={durationDetail} />
      </div>

      <div className="pt-3 border-t border-gray-200 dark:border-gray-800">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 mb-1.5">
          All affinity scores
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
          {[
            { key: "rv", label: "RV", value: row.rv_affinity_applied },
            { key: "cabin", label: "Cabin", value: row.cabin_affinity_applied },
            { key: "glamping", label: "Glamping", value: row.glamping_affinity_applied },
            { key: "tent", label: "Tent", value: row.tent_affinity_applied },
          ].map(({ key, label, value }) => {
            const cfg = ACCOMMODATION_COLORS[key];
            const isBest = row.best_accommodation === key;
            return (
              <div key={key} className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg?.dot || "#9ca3af" }} />
                <span className={`font-semibold ${isBest ? "text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"}`}>
                  {label}
                </span>
                <span className={isBest ? "text-gray-900 dark:text-gray-100 font-bold" : "text-gray-500 dark:text-gray-400"}>
                  {Number(value || 0).toFixed(2)}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {(row.has_playbook || row.url) && (
        <div className="pt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
          {/* Spec (event_intelligence.md:636): the expanded breakdown carries a link
              to the playbook when one exists. Opens the same PlaybookDetailSlideout the
              timeline does, via the page's onOpenPlaybook callback. */}
          {row.has_playbook && onOpenPlaybook && (
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); onOpenPlaybook(row); }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#32B1FF] hover:underline"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="9" y1="13" x2="15" y2="13" />
                <line x1="9" y1="17" x2="13" y2="17" />
              </svg>
              View playbook
            </button>
          )}
          {row.url && (
            <a
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-[#32B1FF] hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Open event source
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// Export column spec — drives both CSV and PDF so they stay in lockstep with each
// other (the on-screen table mixes badges/sparklines that don't translate to a flat
// export, so we emit the underlying values instead). `get` returns a plain string.
const EXPORT_COLUMNS = [
  { header: "Score", get: (row) => String(Math.round(Number(row.opportunity_score || 0))) },
  { header: "Event", get: (row) => row.event_name || "" },
  { header: "Category", get: (row) => formatCategory(row.category) },
  { header: "Subcategory", get: (row) => formatSubcategory(row.subcategory) },
  { header: "Source", get: (row) => row.source || "" },
  { header: "Date", get: (row) => row.start_date || "" },
  { header: "Venue", get: (row) => row.venue_name || "" },
  { header: "Distance (mi)", get: (row) => (row.distance_miles != null ? Number(row.distance_miles).toFixed(1) : "") },
  { header: "Attendance", get: (row) => (row.attendance_estimate ? String(row.attendance_estimate) : "") },
  { header: "Best Accommodation", get: (row) => row.best_accommodation || "" },
  { header: "Lead Time (days)", get: (row) => (row.lead_time_days != null ? String(row.lead_time_days) : "") },
  { header: "Effort", get: (row) => EFFORT_LABELS[scoreToEffort(row.opportunity_score)] },
];

const EXPORT_HEADERS = EXPORT_COLUMNS.map((column) => column.header);
const toExportRow = (row) => EXPORT_COLUMNS.map((column) => column.get(row));

const TOP_N = 10;

const OpportunityBoardPanel = ({ loading, error, opportunities, rangeControl = null, focusRequest = null, onOpenPlaybook = null }) => {
  const [sortState, setSortState] = useState({ key: "opportunity_score", dir: "desc" });
  const [expanded, setExpanded] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [excludedCategories, setExcludedCategories] = useState(() => new Set());
  const rowRefs = useRef(new Map());

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // A no-playbook timeline card was clicked: find its opportunity row, reveal it
  // (show-all if it's past the top-N cutoff), expand its breakdown, and scroll to
  // it. focusRequest carries a `nonce` so re-clicking the same event re-triggers.
  useEffect(() => {
    if (!focusRequest) return;
    const list = Array.isArray(opportunities) ? opportunities : [];
    const match = list.find(
      (opportunity) =>
        (focusRequest.event_id && opportunity.event_id && opportunity.event_id === focusRequest.event_id) ||
        (focusRequest.event_name && opportunity.event_name && opportunity.event_name === focusRequest.event_name)
    );
    if (!match) return;
    const id = match.opportunity_id;
    setShowAll(true);
    setExpanded((prev) => new Set(prev).add(id));
    // Defer scroll until the (possibly newly-shown) row is in the DOM.
    requestAnimationFrame(() => {
      const el = rowRefs.current.get(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [focusRequest, opportunities]);

  const toggleCategory = (cat) => {
    setExcludedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const allCategories = useMemo(() => {
    const set = new Set();
    (Array.isArray(opportunities) ? opportunities : []).forEach((row) => {
      set.add(formatCategory(row.category));
    });
    return Array.from(set).sort();
  }, [opportunities]);

  const sorted = useMemo(() => {
    const baseArr = Array.isArray(opportunities) ? [...opportunities] : [];
    const filteredArr = excludedCategories.size > 0
      ? baseArr.filter((row) => !excludedCategories.has(formatCategory(row.category)))
      : baseArr;
    const { key, dir } = sortState;
    const mult = dir === "asc" ? 1 : -1;
    filteredArr.sort((opportunityA, opportunityB) => {
      let valueA = opportunityA[key];
      let valueB = opportunityB[key];
      if (key === "start_date") {
        valueA = dateSortKey(valueA);
        valueB = dateSortKey(valueB);
      } else {
        valueA = valueA == null ? -Infinity : Number(valueA);
        valueB = valueB == null ? -Infinity : Number(valueB);
        if (Number.isNaN(valueA)) valueA = 0;
        if (Number.isNaN(valueB)) valueB = 0;
      }
      if (valueA < valueB) return -1 * mult;
      if (valueA > valueB) return 1 * mult;
      return 0;
    });
    return filteredArr;
  }, [opportunities, sortState, excludedCategories]);

  const visible = showAll ? sorted : sorted.slice(0, TOP_N);
  const hasMore = sorted.length > TOP_N;

  // Export the full filtered + sorted set the user has narrowed to (every row, not
  // just the top-N currently rendered) — that's the more useful artifact.
  const handleExportCsv = () => {
    downloadCsv(EXPORT_HEADERS, sorted.map(toExportRow), "event-opportunities.csv");
  };
  const handleExportPdf = () => {
    downloadPdf(
      "Event Opportunities",
      EXPORT_HEADERS,
      sorted.map(toExportRow),
      "event-opportunities.pdf",
    );
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-start justify-between gap-4 mb-4 max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:gap-3">
        <div className="max-[499px]:w-full">
          <div className="flex items-center">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Opportunity Scoreboard</h3>
            <InfoTip text="Each event is scored on how well it fits your accommodations, how close it is, how big it'll be, how much lead time you have, and how many days it runs. Toggle All-Time to include events outside the date picker." />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            All scored opportunities. Click a row to see the score breakdown.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 max-[499px]:flex-wrap">
          {rangeControl}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {loading
              ? "Loading…"
              : hasMore && !showAll
                ? `Top ${TOP_N} of ${sorted.length}`
                : `${sorted.length} opportunit${sorted.length === 1 ? "y" : "ies"}`}
          </div>
          {!loading && !error && sorted.length > 0 && (
            <ExportMenu onCsv={handleExportCsv} onPdf={handleExportPdf} />
          )}
        </div>
      </div>

      {allCategories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-wider font-bold text-gray-700 dark:text-gray-200 max-[499px]:w-full">
            Categories
          </span>
          {allCategories.map((cat) => {
            const active = !excludedCategories.has(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => toggleCategory(cat)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  active
                    ? "bg-[#32B1FF]/10 border-[#32B1FF]/40 text-[#32B1FF]"
                    : "bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 line-through"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <SkeletonText lines={8} lineClass="h-12" />
      ) : error ? (
        <ErrorState message={error} />
      ) : sorted.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          {(opportunities || []).length === 0
            ? "No opportunities scored yet."
            : "No opportunities match the current filters."}
        </div>
      ) : (
        <ThinScroll className="overflow-x-auto -mx-1 responsive-card-table-wrapper">
          <table className="w-full text-sm responsive-card-table">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800">
                <th className="w-8 px-2" />
                <SortHeader label="Score" sortKey="opportunity_score" sortState={sortState} setSortState={setSortState} />
                <SortHeader label="Event" sortKey={null} sortState={sortState} setSortState={setSortState} />
                <SortHeader label="Date" sortKey="start_date" sortState={sortState} setSortState={setSortState} />
                <SortHeader label="Venue" sortKey={null} sortState={sortState} setSortState={setSortState} />
                <SortHeader label="Distance" sortKey="distance_miles" sortState={sortState} setSortState={setSortState} align="right" />
                <SortHeader label="Attendance" sortKey="attendance_estimate" sortState={sortState} setSortState={setSortState} align="right" />
                <SortHeader label="Best" sortKey={null} sortState={sortState} setSortState={setSortState} />
                <SortHeader label="Lead" sortKey="lead_time_days" sortState={sortState} setSortState={setSortState} align="right" />
                <SortHeader label="Effort" sortKey={null} sortState={sortState} setSortState={setSortState} />
              </tr>
            </thead>
            <tbody>
              {visible.map((row, index) => {
                const id = row.opportunity_id;
                const isOpen = expanded.has(id);
                const score = Number(row.opportunity_score || 0);
                const sub = formatSubcategory(row.subcategory);
                const stripeBg = isOpen
                  ? 'bg-gray-50 dark:bg-gray-800/30'
                  : index % 2 === 1
                    ? '!bg-gray-50 dark:!bg-gray-800/40'
                    : '!bg-transparent';
                return (
                  <React.Fragment key={id}>
                    <tr
                      ref={(el) => {
                        if (el) rowRefs.current.set(id, el);
                        else rowRefs.current.delete(id);
                      }}
                      {...expandableRowProps({
                        expanded: isOpen,
                        onToggle: () => toggleExpand(id),
                        label: `details for ${row.event_name}`,
                        className: `border-b border-gray-100 dark:border-gray-800/70 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer transition-colors ${stripeBg} ${isOpen ? '' : 'rc-collapsed'}`,
                      })}
                    >
                      <td className={`px-2 text-gray-400 dark:text-gray-500 rc-title-cell ${isOpen ? 'rc-title-open' : ''}`}>
                        <span className="hidden max-[499px]:inline-flex items-center gap-2 w-full">
                          <ScoreBadge score={score} />
                          <span className="font-semibold text-gray-900 dark:text-gray-100 leading-tight flex-1 min-w-0 text-left">
                            {row.event_name}
                          </span>
                          <svg className={`w-4 h-4 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </span>
                        <svg className={`max-[499px]:hidden w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </td>
                      <td className="px-3 py-2.5 rc-hide" data-label="Score">
                        <ScoreBadge score={score} />
                      </td>
                      <td className="px-3 py-2.5 min-w-[240px] rc-hide" data-label="Event">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                          {row.event_name}
                        </div>
                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <span>
                            {formatCategory(row.category)}
                            {sub && <> · {sub}</>}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">·</span>
                          <SourceDot source={row.source} showLabel />
                        </div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap" data-label="Date">
                        <div>
                          <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500 max-[499px]:inline max-[499px]:mr-1.5">
                            {formatMonthShort(row.start_date)}
                          </div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 max-[499px]:inline">
                            {formatDayOfMonth(row.start_date)}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 min-w-[180px]" data-label="Venue">
                        <div className="text-xs text-gray-700 dark:text-gray-200 truncate max-w-[200px]">
                          {row.venue_name || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap" data-label="Distance">
                        {row.distance_miles != null ? `${Number(row.distance_miles).toFixed(1)} mi` : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap" data-label="Attendance">
                        {row.attendance_estimate
                          ? formatAttendance(row.attendance_estimate)
                          : <span className="text-gray-400 dark:text-gray-500" title="Est. unknown">N/A</span>}
                      </td>
                      <td className="px-3 py-2.5" data-label="Best">
                        <AccommodationPill
                          type={row.best_accommodation}
                          fallback={<span className="text-xs text-gray-400 dark:text-gray-500">—</span>}
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap" data-label="Lead">
                        <LeadTimeBadge days={row.lead_time_days} />
                      </td>
                      <td className="px-3 py-2.5" data-label="Effort">
                        <EffortBadge score={score} />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          <ScoreBreakdown row={row} onOpenPlaybook={onOpenPlaybook} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {hasMore && (
            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={() => setShowAll((prev) => !prev)}
                className="px-4 py-1.5 text-xs font-medium rounded-full border border-[#32B1FF]/40 text-[#32B1FF] hover:bg-[#32B1FF]/10 transition-colors"
              >
                {showAll ? `Show top ${TOP_N}` : `Show all ${sorted.length}`}
              </button>
            </div>
          )}
        </ThinScroll>
      )}
    </div>
  );
};

export default OpportunityBoardPanel;
